import { useState } from 'react';
import toast from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon, StarIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import type { ClientContact, ContactType, Client } from '@shared/types';
import { clientContactService } from '../../services/clientContactService';
import { harvestService } from '../../services/harvestService';
import { useConfirm } from '../../hooks/useConfirm';

interface ContactsListProps {
  clientId: string;
  client?: Client; // Optional: full client object to check external source
  contacts: ClientContact[];
  onReload: () => void;
}

const contactTypeColors: Record<ContactType, string> = {
  Signatory: 'bg-purple-100 text-purple-700',
  Purchasing: 'bg-blue-100 text-blue-700',
  Business: 'bg-green-100 text-green-700',
  Technical: 'bg-orange-100 text-orange-700',
};

export function ContactsList({ clientId, client, contacts, onReload }: ContactsListProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [editingContact, setEditingContact] = useState<ClientContact | null>(null);
  const [importing, setImporting] = useState(false);
  const { confirm, ConfirmDialog } = useConfirm();
  
  const canImportFromHarvest = client?.externalSource === 'harvest' && client?.externalId;

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: 'Delete Contact',
      message: 'Are you sure you want to delete this contact?',
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (!confirmed) return;
    
    const toastId = toast.loading('Deleting contact...');
    
    try {
      await clientContactService.deleteContact(id);
      toast.success('Contact deleted successfully!', { id: toastId });
      onReload();
    } catch (error) {
      console.error('Failed to delete contact:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete contact.';
      toast.error(errorMessage, { id: toastId });
    }
  };

  const handleSetPrimary = async (contactId: string) => {
    const toastId = toast.loading('Setting primary contact...');
    
    try {
      await clientContactService.setPrimaryContact(clientId, contactId);
      toast.success('Primary contact updated!', { id: toastId });
      onReload();
    } catch (error) {
      console.error('Failed to set primary contact:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to set primary contact.';
      toast.error(errorMessage, { id: toastId });
    }
  };

  const handleImportFromHarvest = async () => {
    if (!client?.externalId) return;
    
    setImporting(true);
    const toastId = toast.loading('Importing contacts from Harvest...');
    
    try {
      // Fetch contacts from Harvest
      const harvestContacts = await harvestService.fetchHarvestClientContacts(client.externalId);
      
      if (harvestContacts.length === 0) {
        toast.success('No contacts found in Harvest for this client', { id: toastId });
        setImporting(false);
        return;
      }

      let imported = 0;
      let skipped = 0;
      let errors = 0;

      for (const hContact of harvestContacts) {
        try {
          // Check if contact already exists (by email or name)
          const existing = contacts.find(
            c => c.email?.toLowerCase() === hContact.email?.toLowerCase() ||
                 c.name.toLowerCase() === hContact.first_name?.toLowerCase() + ' ' + hContact.last_name?.toLowerCase()
          );
          
          if (existing) {
            console.log(`Skipping ${hContact.first_name} ${hContact.last_name} - already exists`);
            skipped++;
            continue;
          }

          // Map Harvest contact to CLM contact
          const newContact = {
            clientId,
            name: `${hContact.first_name} ${hContact.last_name}`.trim(),
            email: hContact.email || undefined,
            phone: hContact.phone_mobile || hContact.phone_office || undefined,
            jobTitle: hContact.title || undefined,
            contactType: 'Business' as ContactType, // Default, user can change
            isPrimary: false,
            notes: `Imported from Harvest on ${new Date().toLocaleDateString()}`,
          };

          await clientContactService.createContact(newContact);
          console.log(`✅ Imported: ${newContact.name}`);
          imported++;
        } catch (error) {
          console.error(`❌ Failed to import ${hContact.first_name} ${hContact.last_name}:`, error);
          errors++;
        }
      }

      const message = [
        imported > 0 && `${imported} imported`,
        skipped > 0 && `${skipped} skipped`,
        errors > 0 && `${errors} failed`,
      ].filter(Boolean).join(', ');

      if (imported > 0) {
        toast.success(`Contacts: ${message}`, { id: toastId });
        onReload();
      } else {
        toast.error(`Import complete: ${message}`, { id: toastId });
      }
    } catch (error) {
      console.error('Failed to import contacts:', error);
      toast.error('Failed to import contacts from Harvest', { id: toastId });
    } finally {
      setImporting(false);
    }
  };

  if (isAdding || editingContact) {
    return (
      <ContactForm
        clientId={clientId}
        contact={editingContact}
        onSave={() => {
          setIsAdding(false);
          setEditingContact(null);
          onReload();
        }}
        onCancel={() => {
          setIsAdding(false);
          setEditingContact(null);
        }}
      />
    );
  }

  return (
    <div className="border-t pt-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Contacts</h3>
        <div className="flex items-center gap-2">
          {canImportFromHarvest && (
            <button
              onClick={handleImportFromHarvest}
              disabled={importing}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 shadow-sm hover:shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title="Import contacts from Harvest"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              {importing ? 'Importing...' : 'Import from Harvest'}
            </button>
          )}
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            <PlusIcon className="w-4 h-4" />
            Add Contact
          </button>
        </div>
      </div>

      {contacts.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No contacts yet. Add a contact to get started.</p>
      ) : (
        <div className="space-y-3">
          {contacts.map((contact) => {
            const isHarvestContact = contact.externalSource === 'harvest';
            
            return (
              <div
                key={contact.id}
                className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSetPrimary(contact.id)}
                        className="text-gray-400 hover:text-yellow-500"
                        title={contact.isPrimary ? 'Primary contact' : 'Set as primary'}
                      >
                        {contact.isPrimary ? (
                          <StarIconSolid className="w-5 h-5 text-yellow-500" />
                        ) : (
                          <StarIcon className="w-5 h-5" />
                        )}
                      </button>
                      <h4 className="font-medium text-gray-900">{contact.name}</h4>
                      <span
                        className={`px-2 py-0.5 text-xs rounded ${
                          contactTypeColors[contact.contactType] || 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {contact.contactType}
                      </span>
                      {isHarvestContact && (
                        <span
                          className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-700 font-medium"
                          title="Originally imported from Harvest"
                        >
                          🌾 Harvest
                        </span>
                      )}
                    </div>
                    {contact.jobTitle && (
                      <p className="text-sm text-gray-600 ml-7 mt-0.5">{contact.jobTitle}</p>
                    )}
                    {contact.email && (
                      <p className="text-sm text-gray-500 ml-7">📧 {contact.email}</p>
                    )}
                    {contact.phone && (
                      <p className="text-sm text-gray-500 ml-7">📞 {contact.phone}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <button
                      onClick={() => setEditingContact(contact)}
                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                      title="Edit contact"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(contact.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      title="Delete contact"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog />
    </div>
  );
}

interface ContactFormProps {
  clientId: string;
  contact: ClientContact | null;
  onSave: () => void;
  onCancel: () => void;
}

function ContactForm({ clientId, contact, onSave, onCancel }: ContactFormProps) {
  const [name, setName] = useState(contact?.name || '');
  const [email, setEmail] = useState(contact?.email || '');
  const [phone, setPhone] = useState(contact?.phone || '');
  const [jobTitle, setJobTitle] = useState(contact?.jobTitle || '');
  const [contactType, setContactType] = useState<ContactType>(contact?.contactType || 'Business');
  const [isPrimary, setIsPrimary] = useState(contact?.isPrimary || false);
  const [notes, setNotes] = useState(contact?.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    const toastId = toast.loading(contact ? 'Updating contact...' : 'Creating contact...');

    try {
      if (contact) {
        await clientContactService.updateContact(contact.id, {
          name,
          email,
          phone,
          jobTitle,
          contactType,
          isPrimary,
          notes,
        });
        toast.success('Contact updated successfully!', { id: toastId });
      } else {
        await clientContactService.createContact({
          clientId,
          name,
          email,
          phone,
          jobTitle,
          contactType,
          isPrimary,
          notes,
        });
        toast.success('Contact created successfully!', { id: toastId });
      }
      onSave();
    } catch (error) {
      console.error('Failed to save contact:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save contact.';
      toast.error(errorMessage, { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t pt-6 mt-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {contact ? 'Edit Contact' : 'New Contact'}
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Title
            </label>
            <input
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Type *
            </label>
            <select
              value={contactType}
              onChange={(e) => setContactType(e.target.value as ContactType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="Signatory">Signatory</option>
              <option value="Purchasing">Purchasing</option>
              <option value="Business">Business</option>
              <option value="Technical">Technical</option>
            </select>
          </div>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="isPrimary"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
            className="w-4 h-4 text-primary-600 rounded"
          />
          <label htmlFor="isPrimary" className="ml-2 text-sm font-medium text-gray-700">
            Set as primary contact
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Contact'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

